import { NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { API_ENDPOINTS } from '../../config/api';

const navigationItems = [
  { path: '/pipeline', label: 'Filtre', color: 'green' },
  { path: '/vendeurs', label: 'Analyse Vendeur', color: 'purple' },
  { path: '/produits', label: 'Analyse Produit', color: 'orange' },
  { path: '/methodes', label: 'Scrapper d\'ASIN', color: 'teal' },
  { path: '/historique', label: 'Historique', color: 'indigo' },
  { path: '/admin', label: 'Administration', color: 'red', adminOnly: true },
];

export default function Header() {
  const { user, logout } = useAuth();
  const theme = 'light'; // Version simplifiée
  const [quotaData, setQuotaData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const toggleTheme = () => {
    // Version simplifiée - pas de changement de thème pour l'instant
    console.log('Toggle theme clicked');
  };

  // Récupérer les données de quota et d'abonnement
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      // Ne pas appeler les APIs si l'utilisateur n'est pas vérifié
      if (!user.email_verified) return;
      
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        // Récupérer les données de quota
        const quotaResponse = await fetch(API_ENDPOINTS.QUOTA, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (quotaResponse.ok) {
          const quotaData = await quotaResponse.json();
          setQuotaData(quotaData);
        }

        // Récupérer les données d'abonnement
        const subscriptionResponse = await fetch(API_ENDPOINTS.BILLING_SUBSCRIPTION, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          setSubscriptionData(subscriptionData);
        } else if (subscriptionResponse.status === 401) {
          // Token expiré, nettoyer mais ne pas rediriger automatiquement
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
          console.warn('Token expiré - Veuillez vous reconnecter');
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Fermer le menu quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Données de l'utilisateur connecté
  const userProfile = useMemo(() => {
    return user ? {
      name: user.full_name || user.username,
      email: user.email,
      avatar: (user.full_name || user.username).split(' ').map(n => n[0]).join('').toUpperCase(),
      plan: user.subscription_plan || 'free',
      apiQuota: quotaData?.used || 0,
      apiQuotaMax: quotaData?.limit || 500,
      joinDate: new Date(user.created_at).toLocaleDateString('fr-FR'),
      lastLogin: user.last_login ? new Date(user.last_login).toLocaleDateString('fr-FR') : 'Aujourd\'hui',
      totalAnalyses: 1247, // Données mockées pour l'instant
      favoriteAsins: 23,
      totalProfit: 15678.90,
      isAdmin: user.is_admin || false,
      // Informations d'abonnement
      subscriptionStatus: subscriptionData?.subscription_status || 'active',
      cancelAtPeriodEnd: subscriptionData?.subscription_info?.cancel_at_period_end || false,
      currentPeriodEnd: subscriptionData?.subscription_info?.current_period_end || null
    } : {
      name: 'Utilisateur',
      email: '',
      plan: 'BASIC',
      avatar: 'U',
      apiQuota: 0,
      apiQuotaMax: 1000,
      joinDate: '',
      lastLogin: '',
      totalAnalyses: 0,
      favoriteAsins: 0,
      totalProfit: 0,
      isAdmin: false
    };
  }, [user, quotaData, subscriptionData]);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              {/* Logo - PNG favicon */}
              <img 
                src="/favicon.png" 
                alt="Asinder Logo" 
                className="w-8 h-8 mr-3"
                onError={(e) => {
                  // Fallback vers un emoji si l'image ne charge pas
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              <span className="w-8 h-8 mr-3 text-2xl hidden"></span>
              <h1 className="text-xl font-bold text-gray-900">Asinder</h1>
            </div>
          </div>

          {/* Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.filter(item => !item.adminOnly || userProfile.isAdmin).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? `bg-${item.color}-100 text-${item.color}-700 border-b-2 border-${item.color}-500`
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Theme Toggle - Hidden on mobile */}
            <button
              onClick={toggleTheme}
              className="hidden md:block p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              title={`Basculer vers le thème ${theme === 'light' ? 'sombre' : 'clair'}`}
            >
              {theme === 'light' ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {userProfile.avatar}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-gray-900">{userProfile.name}</div>
                  <div className="text-xs text-gray-500">
                    {userProfile.plan}
                    {userProfile.cancelAtPeriodEnd && userProfile.currentPeriodEnd && (
                      <span className="ml-2 text-orange-600">
                        • Annulation le {new Date(userProfile.currentPeriodEnd * 1000).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-lg font-medium">
                        {userProfile.avatar}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{userProfile.name}</h3>
                        <p className="text-sm text-gray-500">{userProfile.email}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {userProfile.plan}
                          </span>
                          {userProfile.cancelAtPeriodEnd && userProfile.currentPeriodEnd && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Annulation prévue le {new Date(userProfile.currentPeriodEnd * 1000).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quota API */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Quota API</span>
                      <span className="text-sm text-gray-500">
                        {userProfile.apiQuota.toLocaleString()} / {userProfile.apiQuotaMax.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(userProfile.apiQuota / userProfile.apiQuotaMax) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Reset le 1er du mois prochain
                    </p>
                  </div>


                  {/* Informations du compte */}
                  <div className="p-4 border-b border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Informations du compte</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Membre depuis</span>
                        <span className="text-gray-900">{new Date(userProfile.joinDate).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Dernière connexion</span>
                        <span className="text-gray-900">{new Date(userProfile.lastLogin).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Plan actuel</span>
                        <span className="text-gray-900">{userProfile.plan}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="p-4">
                    <div className="space-y-2">
                      <button 
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/profil');
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Mon Profil
                      </button>
                      <button 
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/parametres');
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Paramètres
                      </button>
                      <button 
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/billing');
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        Abonnements
                      </button>
                      <button 
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/factures');
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Factures
                      </button>
                      {userProfile && userProfile.isAdmin && (
                        <button 
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/admin');
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          Administration
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Déconnexion
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigationItems.filter(item => !item.adminOnly || userProfile.isAdmin).map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setShowMobileMenu(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-base font-medium rounded-lg ${
                      isActive
                        ? `bg-${item.color}-100 text-${item.color}-700`
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              
              {/* Quota info on mobile */}
              <div className="px-3 py-2 mt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Quota API</div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-900">
                    {userProfile.apiQuota.toLocaleString()} / {userProfile.apiQuotaMax.toLocaleString()}
                  </span>
                  <span className="text-blue-600 font-medium">
                    {userProfile.plan.toUpperCase()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full"
                    style={{ width: `${(userProfile.apiQuota / userProfile.apiQuotaMax) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
